import i18n from '@/lib/i18n';

/**
 * Checks if a Supabase functions.invoke() response indicates
 * that Google re-authentication is required.
 *
 * When an edge function returns HTTP 401 with code GOOGLE_REAUTH_REQUIRED,
 * the Supabase client puts the JSON body in `data` (not `error`).
 */
export function isReauthRequired(res: { data: any; error: any }): boolean {
  return (
    res.data?.code === 'GOOGLE_REAUTH_REQUIRED' ||
    res.error?.code === 'GOOGLE_REAUTH_REQUIRED'
  );
}

/**
 * Extracts Google reauth error details from a Supabase functions.invoke() response.
 *
 * When a Supabase Edge Function returns a non-2xx status (e.g. 401),
 * `supabase.functions.invoke()` sets `data` to `null` and puts the
 * HTTP Response object in `error.context`. The JSON body (containing
 * `code: "GOOGLE_REAUTH_REQUIRED"`) must be read from that Response.
 *
 * Returns the parsed error body if reauth is required, or null otherwise.
 */
export async function extractReauthError(res: { data: any; error: any }): Promise<any> {
  // Check data first (some edge functions may return 200 with error code in body)
  if (res.data?.code === 'GOOGLE_REAUTH_REQUIRED') return res.data;

  if (res.error) {
    // Try error.context as a Response object (Supabase FunctionsHttpError)
    const ctx = (res.error as any)?.context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const body = await ctx.json();
        if (body?.code === 'GOOGLE_REAUTH_REQUIRED') return body;
      } catch {
        // Response body was not JSON or already consumed
      }
    }

    // Fallback: try parsing the error message string as JSON
    const msg = res.error?.message || String(res.error);
    try {
      // Message format may be: "Edge function returned 401: Error, {json}"
      const jsonMatch = msg.match(/\{[\s\S]*"code"\s*:\s*"GOOGLE_REAUTH_REQUIRED"[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed?.code === 'GOOGLE_REAUTH_REQUIRED') return parsed;
      }
    } catch {
      // Not parseable
    }
  }

  return null;
}

/**
 * Navigates the user to Google Accounts page after showing a toast.
 * Used as a fallback when the ReauthDialog popup flow isn't available.
 */
export function handleReauthRedirect(
  toast: (opts: { title: string; description: string; variant?: 'default' | 'destructive' }) => void,
  navigate: (path: string) => void,
) {
  sessionStorage.setItem('redirectAfterReauth', window.location.pathname);
  toast({
    title: i18n.t('common:reauth.redirectTitle'),
    description: i18n.t('common:reauth.redirectDescription'),
    variant: 'destructive',
  });
  setTimeout(() => navigate('/settings?tab=accounts'), 1500);
}
